// Catalog.ID Client — The Recovery Kit entry (`recovery_kit`)
// ============================================================================
// The set's randomness, kept in the account's key bag under the supersecret, so
// that any sheet of a set can be REPRINTED later (registration-restructure
// §16.4). Before round 9 a kit was fresh randomness that existed only for the
// duration of one print: a set could be printed exactly once, at creation, and
// a sheet of one set never combines with a sheet of another.
//
// What is stored — and what is not:
//
//   stored     the polynomial coefficients (the SET), the set id, the per-set
//              verification key, the creation instant, k, n and the sheet
//              labels. Together with the supersecret these re-evaluate any
//              share of the set, at any time, on any node.
//   NOT stored the bundle fingerprint. The entry rides INSIDE every bundle, so
//              it cannot name one; each print stamps its own export's
//              fingerprint on the sheets (§16.4 — a reprinted sheet is
//              share-identical, not byte-identical).
//   NEVER      on a device. Coefficients plus ONE sheet ARE the supersecret,
//              and a device is meant to hold keys, not the root — see
//              `isDeviceStorableKeyId` in `keybag.ts` (§16.5).
//
// Wire contract: the entry's `privateKey` is base64 of the RFC 8785 canonical
// JSON of {@link RecoveryKitEntryData} — the same serializer the signed
// invitation document uses. Canonical, because the entry federates and must
// decode byte-identically on every node and in every later build. Pinned by a
// golden vector in the sibling test.
//
// Generations: `keyIndex` 0 for the first kit; a reissue adds n+1 `active` and
// marks n `revoked`. Exactly one generation is meant to be active; a crash
// between the two writes leaves two, so readers take the ACTIVE entry with the
// HIGHEST `keyIndex` and the next write repairs (§16.9).

import { canonicalize, type CanonicalValue } from '@catalog-canonical-json';
import * as aes from './aes';
import { sssCoefficients } from './sss';
import { generateRecoverySetId, generateVerificationKey } from './recovery-card';
import { getKeyBagEntry, type KeyBag, type KeyBagEntry } from './keybag';

/** Key-bag slot the set lives in. Bag-only on both sides — no `accountpublickey`
 *  row, never wrapped for a delegate, never entitled to anybody (§16.5). */
export const RECOVERY_KIT_KEY_ID = 'recovery_kit';

/** Algorithm tag, named for what the material IS — Shamir shares over GF(2⁸)
 *  with the AES reduction polynomial — following the `derive_root` /
 *  `HKDF-SHA512` precedent. Mirrored server-side in `KnownKeyTypes`. */
export const RECOVERY_KIT_ALGORITHM = 'SSS-GF256';

/** Bytes of supersecret material one Recovery Kit splits (Addendum §4.1): the
 *  44 random characters of the extended supersecret. */
export const RECOVERY_SECRET_BYTES = 44;

/** Length of the per-set verification key (Addendum §4.2). */
const VERIFICATION_KEY_BYTES = 32;

/** The entry's plaintext. Field names and types ARE the wire contract. */
export interface RecoveryKitEntryData {
    /** Format version. Only 1 exists; anything else is refused on decode. */
    v: 1;
    /** Canonical UUID (v7) — the set id every sheet of the set carries. */
    setId: string;
    /** Base64 of the 32-byte per-set verification key. */
    verificationKey: string;
    /** Unix SECONDS the SET was created — the payload's `created_at` and the
     *  "Kit created" line on /recovery. Not the print time (§16.4). */
    createdAt: number;
    thresholdK: number;
    totalSharesN: number;
    /** Base64 of 44 × (k − 1) bytes, BYTE-MAJOR: for secret byte i the
     *  coefficients c₁…c_{k−1} in ascending degree. */
    coefficients: string;
    /** Exactly n sheet purposes, index-aligned with share indexes 1..n. Empty
     *  strings allowed. */
    labels: string[];
}

// ── mint ────────────────────────────────────────────────────────────────────

export interface MintRecoveryKitEntryParams {
    thresholdK: number;
    totalSharesN: number;
    /** Sheet purposes; padded with '' and truncated to exactly n. */
    labels: readonly string[];
    now?: Date;
}

/**
 * Draw a NEW set: fresh coefficients, a fresh set id and a fresh verification
 * key. Pure randomness — no supersecret, no bag, no network. The shares
 * themselves are evaluated later by `buildRecoveryKit`, which is what lets the
 * same set be printed again at any time.
 *
 * TRAP (§16.9): a set is bound to ONE supersecret. Never mint once and
 * re-evaluate under a rotated supersecret — one old and one new sheet of the
 * same index would publish `s_new ⊕ s_old`. A rotation mints a new generation.
 */
export function mintRecoveryKitEntry(params: MintRecoveryKitEntryParams): RecoveryKitEntryData {
    const { thresholdK, totalSharesN } = params;
    validateProfile(thresholdK, totalSharesN);
    const now = params.now ?? new Date();
    return {
        v: 1,
        setId: generateRecoverySetId(now),
        verificationKey: aes.toBase64(generateVerificationKey()),
        createdAt: Math.floor(now.getTime() / 1000),
        thresholdK,
        totalSharesN,
        coefficients: aes.toBase64(sssCoefficients(thresholdK, RECOVERY_SECRET_BYTES)),
        labels: Array.from({ length: totalSharesN }, (_, i) => params.labels[i] ?? ''),
    };
}

// ── encode / decode ─────────────────────────────────────────────────────────

/** Base64 of the canonical JSON — what goes into the bag entry's `privateKey`. */
export function encodeRecoveryKitEntry(data: RecoveryKitEntryData): string {
    validate(data);
    return aes.toBase64(canonicalize(data as unknown as CanonicalValue));
}

/**
 * Read an entry back. Every structural promise the format makes is re-checked
 * here, because this is the only gate between a bag entry (which any node in
 * the federation may have written) and a print that must combine with paper
 * already in custodians' hands.
 */
export function decodeRecoveryKitEntry(privateKeyB64: string): RecoveryKitEntryData {
    let parsed: unknown;
    try {
        parsed = JSON.parse(aes.decodeUtf8(aes.fromBase64(privateKeyB64)));
    } catch {
        throw new Error('The Recovery Kit entry in your key bag is unreadable');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('The Recovery Kit entry in your key bag is unreadable');
    }
    const o = parsed as Record<string, unknown>;
    if (o.v !== 1) {
        throw new Error(`Unsupported Recovery Kit entry version: ${String(o.v)}`);
    }
    const data: RecoveryKitEntryData = {
        v: 1,
        setId: asString(o.setId, 'setId'),
        verificationKey: asString(o.verificationKey, 'verificationKey'),
        createdAt: asInteger(o.createdAt, 'createdAt'),
        thresholdK: asInteger(o.thresholdK, 'thresholdK'),
        totalSharesN: asInteger(o.totalSharesN, 'totalSharesN'),
        coefficients: asString(o.coefficients, 'coefficients'),
        labels: asLabels(o.labels),
    };
    validate(data);
    return data;
}

function validate(data: RecoveryKitEntryData): void {
    if (data.v !== 1) throw new Error(`Unsupported Recovery Kit entry version: ${String(data.v)}`);
    validateProfile(data.thresholdK, data.totalSharesN);

    const expectedCoefficients = RECOVERY_SECRET_BYTES * (data.thresholdK - 1);
    const coefficients = decodeBase64(data.coefficients, 'coefficients');
    if (coefficients.length !== expectedCoefficients) {
        throw new Error(
            `Recovery Kit entry holds ${coefficients.length} coefficient bytes; a ${data.thresholdK}-of-`
            + `${data.totalSharesN} set needs ${expectedCoefficients}`);
    }

    const key = decodeBase64(data.verificationKey, 'verificationKey');
    if (key.length !== VERIFICATION_KEY_BYTES) {
        throw new Error(
            `Recovery Kit entry holds a ${key.length}-byte verification key; expected `
            + `${VERIFICATION_KEY_BYTES}`);
    }

    if (data.labels.length !== data.totalSharesN) {
        throw new Error(
            `Recovery Kit entry holds ${data.labels.length} sheet labels; a set of `
            + `${data.totalSharesN} sheets needs exactly that many`);
    }
    if (!Number.isInteger(data.createdAt) || data.createdAt < 0) {
        throw new Error('Recovery Kit entry has an invalid creation time');
    }
}

function validateProfile(thresholdK: number, totalSharesN: number): void {
    if (!Number.isInteger(thresholdK) || thresholdK < 2) {
        throw new Error('Recovery Kit threshold must be an integer of at least 2');
    }
    if (!Number.isInteger(totalSharesN) || totalSharesN < thresholdK || totalSharesN > 255) {
        throw new Error('Recovery Kit total shares must be an integer between the threshold and 255');
    }
}

// ── the bag half ────────────────────────────────────────────────────────────

/** Wrap the plaintext into the key-bag entry shape the bag writes expect. */
export function recoveryKitBagEntry(
    data: RecoveryKitEntryData, keyIndex = 0, status: 'active' | 'revoked' = 'active',
): KeyBagEntry {
    return {
        keyId: RECOVERY_KIT_KEY_ID,
        algorithm: RECOVERY_KIT_ALGORITHM,
        privateKey: encodeRecoveryKitEntry(data),
        keyIndex,
        status,
    };
}

/** The ACTIVE `recovery_kit` entry with the highest `keyIndex`, or null. Never
 *  a revoked generation: those exist only so a bag write is never destructive
 *  (§16.4), and nothing prints from them. */
export function activeRecoveryKitEntry(bag: KeyBag): KeyBagEntry | null {
    return getKeyBagEntry(bag, RECOVERY_KIT_KEY_ID) ?? null;
}

/** {@link activeRecoveryKitEntry}, decoded. Throws when an entry is present but
 *  unreadable — a caller must never quietly fall back to minting a new set over
 *  paper that is already in custodians' hands. */
export function readRecoveryKitEntry(
    bag: KeyBag,
): { entry: KeyBagEntry; data: RecoveryKitEntryData } | null {
    const entry = activeRecoveryKitEntry(bag);
    if (!entry) return null;
    return { entry, data: decodeRecoveryKitEntry(entry.privateKey) };
}

/** The `keyIndex` the NEXT generation takes: one past the highest present,
 *  revoked generations included, so a reissue never collides with the
 *  `(accountid, keyid, keyindex)` unique constraint. */
export function nextRecoveryKitKeyIndex(bag: KeyBag): number {
    const indexes = bag.entries
        .filter(e => e.keyId === RECOVERY_KIT_KEY_ID)
        .map(e => e.keyIndex ?? 0);
    return indexes.length === 0 ? 0 : Math.max(...indexes) + 1;
}

// ── helpers ─────────────────────────────────────────────────────────────────

function asString(v: unknown, field: string): string {
    if (typeof v !== 'string') throw new Error(`Recovery Kit entry field '${field}' must be a string`);
    return v;
}

function asInteger(v: unknown, field: string): number {
    if (typeof v !== 'number' || !Number.isInteger(v)) {
        throw new Error(`Recovery Kit entry field '${field}' must be an integer`);
    }
    return v;
}

function asLabels(v: unknown): string[] {
    if (!Array.isArray(v) || v.some(x => typeof x !== 'string')) {
        throw new Error('Recovery Kit entry field \'labels\' must be an array of strings');
    }
    return v as string[];
}

function decodeBase64(value: string, field: string): Uint8Array {
    try {
        return aes.fromBase64(value);
    } catch {
        throw new Error(`Recovery Kit entry field '${field}' is not valid base64`);
    }
}

/** What a create-or-reissue writes: the generation the NEW entry takes, and the
 *  generations it supersedes. */
export interface RecoveryKitGenerationPlan {
    /** `keyIndex` for the entry being added — 0 for a first kit. */
    keyIndex: number;
    /** Currently-ACTIVE generations to mark `revoked` after the add. Normally
     *  one (a reissue) or none (a first kit); two after a crash between an
     *  earlier add and its revoke, which this plan repairs (§16.9). Already
     *  revoked generations are left alone — they are retained, not rewritten. */
    supersede: { keyId: string; keyIndex: number }[];
}

/**
 * Plan the bag writes for creating or reissuing a Recovery Kit.
 *
 * Order matters at the call site: ADD first, then revoke. A crash between the
 * two leaves two active generations, which readers resolve by highest
 * `keyIndex` and the next write repairs; a crash the other way round would
 * leave the account with no active set at all.
 */
export function planRecoveryKitGeneration(bag: KeyBag): RecoveryKitGenerationPlan {
    return {
        keyIndex: nextRecoveryKitKeyIndex(bag),
        supersede: bag.entries
            .filter(e => e.keyId === RECOVERY_KIT_KEY_ID && (e.status ?? 'active') === 'active')
            .map(e => ({ keyId: RECOVERY_KIT_KEY_ID, keyIndex: e.keyIndex ?? 0 }))
            .sort((a, b) => a.keyIndex - b.keyIndex),
    };
}
